<template>
  <div
    :class="{
      'grid grid-cols-12 lg:gap-30px': true,
    }"
  >
    <div :class="buttonColumnClasses">
      <ParallaxThemeComponent :theme="buttonsBlockTheme">
        <div :class="sectionClasses">
          <Button
            v-for="(button, index) in hoverButtonRow || []"
            :key="index"
            v-bind="{ buttonProps: button }"
            :class="{
              'w-full max-w-64 flex-grow md:flex-grow-0': true,
            }"
            @mouseover="hoveredButtonImage = button.image"
            @mouseleave="hoveredButtonImage = hoverButtonRow?.[0]?.image || null"
          ></Button>
        </div>
      </ParallaxThemeComponent>
    </div>
    <div :class="imageColumnClasses">
      <ParallaxThemeComponent :theme="imageBlock">
        <Transition
          name="fade"
          mode="out-in"
        >
          <PtcPicture
            v-if="hoveredButtonImage && hoveredButtonImage.asset?._ref"
            :key="hoveredButtonImage.asset._ref"
            provider="sanity"
            :src="hoveredButtonImage?.asset._ref"
            :img-attrs="{
              class: { 'block mx-auto object-contain h-full max-w-full': true },
            }"
            :modifiers="{
              crop: hoveredButtonImage.crop,
              hotspot: hoveredButtonImage.hotspot,
            }"
            :alt="hoveredButtonImage.altText || ''"
          />
        </Transition>
      </ParallaxThemeComponent>
    </div>
  </div>
</template>

<script setup lang="ts">
import ParallaxThemeComponent from '~/components/Molecules/Themes/ParallaxTheme.vue';
import Button from '~/components/Atmos/Button/Button.vue';
import PtcPicture from '~/components/Atmos/PtcPicture/PtcPicture.vue';
import type { HoverButtonRow, ParallaxTheme } from '~/sanity/types';
type HoverButtonRowProps = HoverButtonRow & {
  buttonsBlockWidth?: Width;
  buttonsBlockAlignment?: Alignment;
  buttonsBlockTheme?: ParallaxTheme;
  imageBlock?: ParallaxTheme;
};
const props = defineProps<HoverButtonRowProps>();
const { alignment, hoverButtonRow, buttonsBlockWidth, buttonsBlockAlignment } = props;
const hoveredButtonImage = ref(hoverButtonRow?.[0]?.image || null);
const buttonWidth = ref<Width>(buttonsBlockWidth || '6');
const buttonAlignment = ref<Alignment>(buttonsBlockAlignment || 'left');

const buttonColumnClasses = computed(() => ({
  [useWidthClass(buttonWidth.value) ?? '']: true,
  [useAlignmentClass(buttonAlignment.value) ?? '']: true,
  'content-center col-span-12 order-1': true,
}));
const imageColumnClasses = computed(() => ({
  [useInverseWidthClass(buttonWidth.value) ?? '']: true,
  [useAlignmentSecondaryClass(buttonAlignment.value) ?? '']: true,
  'content-center col-span-12 lg:h-screen max-h-[1000px] order-0 pb-10 lg:pb-0': true,
}));

const sectionClasses = computed(() => ({
  'container px-0 button-wrapper flex gap-3 items-center flex-wrap flex-col h-full': true,
  [alignmentClasses[alignment] ?? alignmentClasses.center]: true,
}));
const alignmentClasses: Record<Alignment, string> = {
  left: 'text-left justify-start',
  center: 'text-center justify-center',
  right: 'text-right justify-end',
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.1s ease-in-out;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
